<script setup lang="ts">
import type { SwitchRootEmits, SwitchRootProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { SwitchRoot, SwitchThumb, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';

const props = withDefaults(defineProps<SwitchRootProps & {
    checked?: boolean | null;
    modelValue?: boolean | null;
    class?: HTMLAttributes['class'];
} >(), { checked: undefined, modelValue: undefined });
const emits = defineEmits<SwitchRootEmits & {
    'update:modelValue': [payload: boolean];
    'update:checked': [payload: boolean];
}>();

const delegatedProps = reactiveOmit(props, 'class', 'checked', 'modelValue');
const forwarded = useForwardPropsEmits(delegatedProps);

const resolvedChecked = computed(() => (
    props.checked ?? props.modelValue ?? false
));

function handleUpdate(value: boolean) {
    emits('update:modelValue', value);
    emits('update:checked', value);
}
</script>

<template>
    <SwitchRoot
        v-bind="forwarded"
        :model-value="resolvedChecked"
        :class="cn(
            'peer inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-transparent bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/60 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary',
            props.class,
        )"
        @update:model-value="handleUpdate"
    >
        <SwitchThumb
            class="pointer-events-none block size-5 rounded-full bg-background shadow-sm ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
        />
    </SwitchRoot>
</template>
